import { type FormEvent, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { EMAIL_OTP_MODE_KEY, EMAIL_OTP_PENDING_KEY } from "./EmailOtpEmailPage";
import { api } from "../lib/api";
import { AppError } from "../lib/errors";
import { storage } from "../lib/storage";
import { UI_TEXT_TONE } from "../lib/uiTone";

function parseOtpErrorReason(responseText: string | undefined): "invalid" | "expired" | null {
  if (!responseText) return null;
  try {
    const parsed = JSON.parse(responseText) as { reason?: unknown; data?: { reason?: unknown } };
    const reason = parsed?.reason ?? parsed?.data?.reason;
    if (reason === "invalid" || reason === "expired") return reason;
  } catch {
    // ignore parse error
  }
  return null;
}

function isValidEmail(value: string): boolean {
  return value.length > 0 && value.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

type EntryErrorKind = "account_not_found" | "already_registered" | null;

export default function EmailOtpVerifyPage() {
  const navigate = useNavigate();
  const rawEmail = sessionStorage.getItem(EMAIL_OTP_PENDING_KEY) ?? "";
  const email = rawEmail.trim();
  const rawMode = sessionStorage.getItem(EMAIL_OTP_MODE_KEY);
  const mode: 'auto' | 'login' | 'register' =
    rawMode === 'login' || rawMode === 'register' ? rawMode : 'auto';
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entryErrorKind, setEntryErrorKind] = useState<EntryErrorKind>(null);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const submittedRef = useRef(false);

  if (!isValidEmail(email)) {
    navigate("/email-otp/email", { replace: true });
    return null;
  }

  const trimmedCode = code.trim();
  const canSubmit = trimmedCode.length === 6 && !loading;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || submittedRef.current) return;

    setLoading(true);
    setError(null);
    setEntryErrorKind(null);
    submittedRef.current = true;
    let succeeded = false;

    try {
      const result = await api.emailOtpVerify({ email, code: trimmedCode, mode });
      storage.setAuth({ userId: result.userId, accessToken: result.accessToken, refreshToken: result.refreshToken, authProvider: "email" });
      sessionStorage.removeItem(EMAIL_OTP_PENDING_KEY);
      sessionStorage.removeItem(EMAIL_OTP_MODE_KEY);
      succeeded = true;
      if (result.isNewUser) {
        navigate("/onboarding", { replace: true });
      } else {
        navigate("/home", { replace: true });
      }
    } catch (errorValue) {
      submittedRef.current = false;
      if (errorValue instanceof AppError) {
        if (errorValue.meta.status === 429) {
          setError("しばらく時間をおいてからお試しください。");
        } else if (errorValue.code === "NOT_FOUND") {
          setEntryErrorKind("account_not_found");
        } else if (errorValue.code === "CONFLICT") {
          setEntryErrorKind("already_registered");
        } else if (errorValue.code === "BAD_REQUEST") {
          const reason = parseOtpErrorReason(errorValue.meta.responseText);
          if (reason === "expired") {
            setError("有効期限が切れています。もう一度コードを送信してください。");
          } else {
            setError("確認コードが正しくありません。");
          }
        } else {
          setError("確認に失敗しました。時間をおいて再度お試しください。");
        }
      } else {
        setError("確認に失敗しました。時間をおいて再度お試しください。");
      }
    } finally {
      if (!succeeded) setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resending) return;
    setResending(true);
    setResendMessage(null);
    setError(null);

    try {
      await api.emailOtpResend({ email });
      setResendMessage("確認コードを再送しました。メールをご確認ください。");
    } catch (errorValue) {
      if (errorValue instanceof AppError && errorValue.meta.status === 429) {
        setResendMessage("しばらく時間をおいてからお試しください。");
      } else {
        setResendMessage("再送に失敗しました。時間をおいて再度お試しください。");
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(244,227,215,0.9),_rgba(253,248,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-md items-center justify-center">
        <div className="w-full rounded-[2.4rem] border border-white/80 bg-white/90 p-6 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <div className="mb-6">
            <p className={`text-[11px] font-bold tracking-[0.12em] ${UI_TEXT_TONE.eyebrow}`}>SIGN IN / SIGN UP</p>
            <h1 className={`mt-2 text-2xl font-black tracking-tight ${UI_TEXT_TONE.title}`}>
              メールに届いた確認コードを入力してください
            </h1>
            <p className={`mt-2 text-sm leading-6 ${UI_TEXT_TONE.body}`}>
              {email} に確認コードを送信しました。
            </p>
          </div>

          {entryErrorKind === "account_not_found" ? (
            <div className="mb-4 rounded-xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
              <p className="font-medium">このメールアドレスでの登録が見つかりません。</p>
              <p className="mt-1">
                <Link to="/register" className="font-bold underline underline-offset-2">新規登録はこちら</Link>
              </p>
            </div>
          ) : entryErrorKind === "already_registered" ? (
            <div className="mb-4 rounded-xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
              <p className="font-medium">このメールアドレスはすでに登録済みです。</p>
              <p className="mt-1">
                <Link to="/login" className="font-bold underline underline-offset-2">ログインはこちら</Link>
              </p>
            </div>
          ) : error ? (
            <p className="mb-4 rounded-xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm font-medium text-[#A1493E]">
              {error}
            </p>
          ) : null}

          <form onSubmit={(e) => void handleSubmit(e)}>
            <label htmlFor="email-otp-code-input" className="text-sm font-black text-[#5D4D46]">
              確認コード（6桁）
            </label>
            <input
              id="email-otp-code-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, ""));
                setError(null);
                setEntryErrorKind(null);
              }}
              className="mt-3 w-full rounded-[1.1rem] border border-[#EADBC8] bg-[#FFFCFA] px-4 py-3 text-center text-lg font-black tracking-[0.4em] text-[#4A3E39] outline-none transition focus:border-[#D1907B] focus:bg-white"
              placeholder="000000"
              autoFocus
              disabled={loading}
              autoComplete="one-time-code"
            />

            <button
              type="submit"
              disabled={!canSubmit}
              className="mt-5 w-full rounded-[1.6rem] bg-[#5D4D46] px-4 py-4 text-sm font-black text-[#FDF8F5] shadow-lg transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? "確認中..." : "確認する"}
            </button>
          </form>

          <div className="mt-5 text-center">
            {resendMessage ? (
              <p className="text-xs font-medium text-[#7B6861]" aria-live="polite">
                {resendMessage}
              </p>
            ) : (
              <button
                type="button"
                onClick={() => void handleResend()}
                disabled={resending}
                className="text-sm font-bold text-[#A67C52] underline underline-offset-2 transition hover:text-[#7C5A4E] disabled:opacity-60"
              >
                {resending ? "再送中..." : "コードを再送する"}
              </button>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
